<template>
  <div class="settings--content">
    <loading-state v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form v-else class="row" @submit.prevent="">
        <div v-if="isCreating" class="create-template-container">
          <div class="create-selections">
            <div class="radio-group">
              <label class="radio-option">
                <input type="radio" name="template-category" value="marketing" v-model="selectedCategory" />
                {{ $t('WHATSAPP_TEMPLATES.CREATOR.RADIO.MARKETING') }}
              </label>
              <label class="radio-option">
                <input type="radio" name="template-category" value="utility" v-model="selectedCategory" />
                {{ $t('WHATSAPP_TEMPLATES.CREATOR.RADIO.UTILITY') }}
              </label>
            </div>
          </div>
          <input
            v-model="newTemplateName"
            type="text"
            class="templates__search-input"
            :placeholder="$t('WHATSAPP_TEMPLATES.CREATOR.NAME.PLACEHOLDER')"
            pattern="^[a-z0-9_]+$"
            :title="$t('WHATSAPP_TEMPLATES.CREATOR.NAME.HELP')"
            @input="onNameInput"
          />
          <p class="helper-text">{{ $t('WHATSAPP_TEMPLATES.CREATOR.NAME.HELP') }}</p>
          <p v-if="nameWarning" class="warning-text">{{ $t('WHATSAPP_TEMPLATES.CREATOR.NAME.WARNING') }}</p>
          <div class="field-group">
            <label class="field-label">{{ $t('WHATSAPP_TEMPLATES.CREATOR.LANGUAGE') }}</label>
            <div class="dropdown-group">
              <select v-model="selectedLanguage" class="select-input">
                <option :value="'pt_BR'">{{ $t('WHATSAPP_TEMPLATES.CREATOR.LANGUAGES.pt_BR') }}</option>
                <option :value="'en'">{{ $t('WHATSAPP_TEMPLATES.CREATOR.LANGUAGES.en') }}</option>
              </select>
            </div>
          </div>
          <div class="field-group">
            <label class="field-label">{{ $t('WHATSAPP_TEMPLATES.CREATOR.HEADER.LABEL') }}</label>
            <div class="dropdown-group">
              <select v-model="headerType" class="select-input" @change="onHeaderTypeChange">
                <option value="text">{{ $t('WHATSAPP_TEMPLATES.CREATOR.HEADER.TYPES.text') }}</option>
                <option value="image">{{ $t('WHATSAPP_TEMPLATES.CREATOR.HEADER.TYPES.image') }}</option>
                <option value="video">{{ $t('WHATSAPP_TEMPLATES.CREATOR.HEADER.TYPES.video') }}</option>
                <option value="document">{{ $t('WHATSAPP_TEMPLATES.CREATOR.HEADER.TYPES.document') }}</option>
              </select>
            </div>
            <div v-if="headerType === 'text'">
              <input
                v-model="headerText"
                type="text"
                class="text-input"
                :placeholder="$t('WHATSAPP_TEMPLATES.CREATOR.HEADER.PLACEHOLDER')"
                :maxlength="headerMax"
              />
              <div class="field-meta">
                <span class="char-counter">{{ headerText.length }}/{{ headerMax }}</span>
              </div>
            </div>
            <div v-else class="header-media-upload">
              <input
                ref="headerFileInput"
                type="file"
                class="text-input"
                :accept="headerAccept"
                @change="onHeaderFileSelected"
              />
              <div class="field-meta" style="justify-content: space-between; align-items: center;">
                <div>
                  <span v-if="headerUploadState === 'uploading'" class="char-counter">{{ $t('WHATSAPP_TEMPLATES.CREATOR.HEADER.UPLOAD.SENDING') }}</span>
                  <span v-else-if="headerUploadState === 'success'" class="char-counter">{{ $t('WHATSAPP_TEMPLATES.CREATOR.HEADER.UPLOAD.SENT') }}</span>
                  <span v-else-if="headerUploadState === 'error'" class="error-text">{{ $t('WHATSAPP_TEMPLATES.CREATOR.HEADER.UPLOAD.ERROR') }}</span>
                </div>
                <div v-if="headerHandle" class="char-counter">{{ headerMediaFile && headerMediaFile.name }}</div>
              </div>
              <div v-if="headerMediaPreviewUrl" style="margin-top: var(--space-one);">
                <img v-if="headerType === 'image'" :src="headerMediaPreviewUrl" style="max-width: 200px; border: 1px solid var(--s-100); border-radius: var(--border-radius-small);" />
                <video v-else-if="headerType === 'video'" :src="headerMediaPreviewUrl" width="260" controls style="display:block;" />
                <div v-else-if="headerType === 'document'">
                  <button v-if="headerMediaIsPdf" @click="openPdfInNewTab" class="pdf-preview-button">
                    <vue-pdf-embed
                      :source="headerMediaPreviewUrl"
                      :width="200"
                      :scale="1"
                      :page="1"
                    />
                  </button>
                  <a v-else :href="headerMediaPreviewUrl" target="_blank" rel="noopener">{{ $t('WHATSAPP_TEMPLATES.CREATOR.HEADER.UPLOAD.PREVIEW') }}</a>
                </div>
              </div>
            </div>
          </div>
          <div class="field-group">
            <label class="field-label">{{ $t('WHATSAPP_TEMPLATES.CREATOR.BODY.LABEL') }}</label>
            <textarea
              ref="bodyInput"
              v-model="bodyText"
              class="textarea"
              rows="6"
              :maxlength="bodyMax"
            ></textarea>
            <div class="formatting-wrap">
            <div class="formatting-buttons">
              <button type="button" class="format-btn" @click="toggleEmojiPicker" :title="$t('WHATSAPP_TEMPLATES.CREATOR.BODY.FORMAT.EMOJI')">😊</button>
              <button type="button" class="format-btn" @click="applyFormat('bold')" :title="$t('WHATSAPP_TEMPLATES.CREATOR.BODY.FORMAT.BOLD')">
                <strong>N</strong>
              </button>
              <button type="button" class="format-btn" @click="applyFormat('italic')" :title="$t('WHATSAPP_TEMPLATES.CREATOR.BODY.FORMAT.ITALIC')">
                <em>I</em>
              </button>
              <button type="button" class="format-btn" @click="applyFormat('strikethrough')" :title="$t('WHATSAPP_TEMPLATES.CREATOR.BODY.FORMAT.STRIKE')">
                <s>T</s>
              </button>
              <button type="button" class="format-btn" @click="applyFormat('monospace')" :title="$t('WHATSAPP_TEMPLATES.CREATOR.BODY.FORMAT.MONO')">
                <code>M</code>
              </button>
              <span class="add-var-link" @click="addVariable">{{ $t('WHATSAPP_TEMPLATES.CREATOR.BODY.FORMAT.ADD_VARIABLE') }}</span>
            </div>
            <emoji-input
              v-if="showEmojiPicker"
              v-on-clickaway="hideEmojiPicker"
              class="emoji-dialog"
              :on-click="emojiOnClick"
            />
            </div>
            <div class="toolbar">
              <span class="char-counter">{{ bodyText.length }}/{{ bodyMax }}</span>
            </div>
            <div v-if="bodyVariableErrors.length">
              <p
                v-for="(err, idx) in bodyVariableErrors"
                :key="`body-error-${idx}`"
                class="error-text"
              >
                {{ err }}
              </p>
            </div>
            <div v-if="variableNumbers.length" class="variables-examples">
              <label class="field-label">{{ $t('WHATSAPP_TEMPLATES.CREATOR.BODY.VARIABLES.LABEL') }}</label>
              <div
                v-for="(num, idx) in variableNumbers"
                :key="`var-example-${num}`"
                class="variable-example-row"
              >
                <label :for="`var-example-${num}`" class="field-label small">
                  {{ tokenFor(num) }}
                </label>
                <input
                  :id="`var-example-${num}`"
                  v-model="variableExamples[idx]"
                  type="text"
                  class="text-input"
                  :placeholder="$t('WHATSAPP_TEMPLATES.CREATOR.BODY.VARIABLES.EXAMPLE_FOR', { token: tokenFor(num) })"
                />
              </div>
            </div>
          </div>
          <div class="field-group">
            <label class="field-label">{{ $t('WHATSAPP_TEMPLATES.CREATOR.FOOTER.LABEL') }}</label>
            <input
              v-model="footerText"
              type="text"
              class="text-input"
              :placeholder="$t('WHATSAPP_TEMPLATES.CREATOR.FOOTER.PLACEHOLDER')"
              :maxlength="footerMax"
            />
            <div class="field-meta">
              <span class="char-counter">{{ footerText.length }}/{{ footerMax }}</span>
            </div>
          </div>
          <div class="field-group">
            <label class="field-label">{{ $t('WHATSAPP_TEMPLATES.CREATOR.BUTTONS.LABEL') }}</label>
            <div class="dropdown-group" style="display:flex; gap: var(--space-small);">
              <select v-model="newButtonType" class="select-input" :key="buttons.length">
                <option value="">{{ $t('WHATSAPP_TEMPLATES.CREATOR.BUTTONS.SELECT_PLACEHOLDER') }}</option>
                <option v-for="opt in availableButtonOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <button type="button" class="button button--secondary" :disabled="!newButtonType" @click="addButton">
                {{ $t('WHATSAPP_TEMPLATES.CREATOR.BUTTONS.ADD') }}
              </button>
            </div>
            <div v-for="btn in buttons" :key="btn.id" class="button-input-group">
              <div class="flex-between">
                <span class="strong">{{ labelForType(btn.type) }}</span>
                <button type="button" class="button button--text" @click="removeButton(btn.id)">{{ $t('WHATSAPP_TEMPLATES.CREATOR.BUTTONS.REMOVE') }}</button>
              </div>
              <div v-if="showValueField(btn.type)">
                <label :for="`btn-value-${btn.id}`" class="field-label">{{ dynamicLabelFor(btn.type) }}</label>
                <phone-input
                  v-if="btn.type === 'PHONE_NUMBER'"
                  :value="btn.phoneNumber || ''"
                  :placeholder="dynamicPlaceholderFor(btn.type)"
                  @input="onPhoneInputChange(btn, $event)"
                  @setCode="onPhoneCodeChange(btn, $event)"
                />
                <input
                  v-else
                  :id="`btn-value-${btn.id}`"
                  v-model="btn.inputValue"
                  type="text"
                  class="text-input"
                  :placeholder="dynamicPlaceholderFor(btn.type)"
                />
              </div>
              <div v-if="showTextField(btn.type)">
                <label :for="`btn-text-${btn.id}`" class="field-label" style="margin-top: var(--space-one);">{{ $t('WHATSAPP_TEMPLATES.CREATOR.BUTTONS.TEXT_LABEL') }}</label>
                <input
                  :id="`btn-text-${btn.id}`"
                  v-model="btn.displayText"
                  type="text"
                  class="text-input"
                  :placeholder="$t('WHATSAPP_TEMPLATES.CREATOR.BUTTONS.TEXT_LABEL')"
                />
              </div>
            </div>
          </div>
          <div class="create-actions">
            <button type="button" class="button button--secondary" @click="isCreating = false">
              {{ $t('WHATSAPP_TEMPLATES.CREATOR.CANCEL') }}
            </button>
            <woot-button
              type="button"
              :is-loading="isSubmitting"
              :is-disabled="!isFormValid"
              class-names="button button--primary"
              style="margin-left: var(--space-small);"
              @click="onSubmitTemplate"
            >
              {{ $t('WHATSAPP_TEMPLATES.CREATOR.SEND') }}
            </woot-button>
          </div>
        </div>
        <div v-else>
          <div v-if="isSyncingAfterDelete" class="syncing-container">
            <div class="syncing-overlay">
              <spinner size="large" />
              <p class="syncing-text">{{ $t('WHATSAPP_TEMPLATES.DELETE.SYNCING') }}</p>
            </div>
          </div>
          <template v-else>
            <div class="actions-row">
              <button
                type="button"
                class="button button--primary"
                style="margin-right: var(--space-small);"
                @click="redirectToMetaCategoryUpdates"
              >
                {{ $t('WHATSAPP_TEMPLATES.CREATOR.REVISE_TEMPLATE') }}
              </button>
              <button
                type="button"
                class="button button--primary"
                @click="onCreateTemplate"
              >
                {{ $t('WHATSAPP_TEMPLATES.CREATOR.CREATE_TEMPLATE') }}
              </button>
            </div>
            <div class="templates__list-search">
              <fluent-icon icon="search" class="search-icon" size="16" />
              <input
                v-model="query"
                type="search"
                :placeholder="$t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
                class="templates__search-input"
              />
            </div>
            <div class="template__list-container">
              <div v-for="(template, i) in filteredTemplateMessages" :key="template.id">
                <div class="template__list-item" :class="statusClass(template)">
                  <div>
                    <div class="flex-between">
                      <p class="label-title">{{ template.name }}</p>
                      <div class="template-actions">
                        <span class="label-lang label">
                          {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.LANGUAGE') }} : {{ template.language }}
                        </span>
                        <span class="label-lang label">
                          {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.STATUS') }} : {{ ptStatus(template) }}
                        </span>
                        <woot-button
                          icon="delete"
                          variant="smooth"
                          size="small"
                          color-scheme="alert"
                          class="button button--danger button--small delete-button"
                          @click="openDeleteModal(template)"
                          :disabled="isDeletingTemplate"
                        >
                        </woot-button>
                      </div>
                    </div>
                    <div>
                      <p class="strong">{{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.TEMPLATE_BODY') }}</p>
                      <p class="label-body">{{ getTemplateBody(template) }}</p>
                    </div>
                    <div class="label-category">
                      <p class="strong">{{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.CATEGORY') }}</p>
                      <p>{{ template.category }}</p>
                    </div>
                  </div>
                </div>
                <hr v-if="i != filteredTemplateMessages.length - 1" :key="`hr-${i}`" />
              </div>
              <div v-if="!filteredTemplateMessages.length">
                <p>
                  {{ $t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
                  <strong>{{ query }}</strong>
                </p>
              </div>
            </div>
          </template>
        </div>
    </form>

    <!-- Modal de Confirmação de Exclusão -->
    <woot-delete-modal
      v-if="showDeleteModal"
      :show.sync="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeleteTemplate"
      :title="$t('WHATSAPP_TEMPLATES.DELETE.CONFIRM_TITLE')"
      :message="$t('WHATSAPP_TEMPLATES.DELETE.CONFIRM_MESSAGE', { name: templateToDelete && templateToDelete.name })"
      :confirm-text="$t('WHATSAPP_TEMPLATES.DELETE.CONFIRM')"
      :reject-text="$t('WHATSAPP_TEMPLATES.DELETE.CANCEL')"
    />
  </div>
  </template>

  <script>
  import { mapGetters } from 'vuex';
  import { mixin as clickaway } from 'vue-clickaway';
  import axios from 'axios';
  import SettingsSection from 'dashboard/components/SettingsSection';
  import LoadingState from 'dashboard/components/widgets/LoadingState';
  import Spinner from 'shared/components/Spinner.vue';
  import alertMixin from 'shared/mixins/alertMixin';
  import WootButton from 'dashboard/components/ui/WootButton.vue';
  import WootDeleteModal from 'dashboard/components/widgets/modal/DeleteModal.vue';
  import PhoneInput from 'dashboard/components/widgets/forms/PhoneInput.vue';
  import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
  const EmojiInput = () => import('shared/components/emoji/EmojiInput');

  export default {
    components: {
      LoadingState,
      SettingsSection,
      EmojiInput,
      WootButton,
      WootDeleteModal,
      PhoneInput,
      Spinner,
      VuePdfEmbed,
    },
    mixins: [alertMixin, clickaway],
    props: {
      inbox: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        selectedAgentBotId: null,
        query: '',
        isCreating: false,
        newTemplateName: '',
        nameWarning: '',
        selectedLanguage: 'pt_BR',
        selectedCategory: 'marketing',
        selectedTemplateType: 'padrao',
        headerType: 'text',
        headerText: '',
        headerMediaFile: null,
        headerMediaPreviewUrl: '',
        headerHandle: '',
        headerUploadState: 'idle',
        showEmojiPicker: false,
        bodyText: '',
        footerText: '',
        headerMax: 60,
        bodyMax: 1024,
        footerMax: 60,
        newButtonType: '',
        buttons: [],
        variableExamples: [],
        prevVariableNumbers: [],
        buttonOptionValues: [
          'QUICK_REPLY',
          'URL',
          'VOICE_CALL',
          'PHONE_NUMBER',
        ],
        isSubmitting: false,
        showDeleteModal: false,
        templateToDelete: null,
        isSyncingAfterDelete: false,
      };
    },
    computed: {
      ...mapGetters({
        agentBots: 'agentBots/getBots',
        uiFlags: 'agentBots/getUIFlags',
        templatesUIFlags: 'templates/getUIFlags',
      }),
      isDeletingTemplate() {
        return this.templatesUIFlags.isDeleting;
      },
      isFormValid() {
        return (
          this.newTemplateName &&
          /^[a-z0-9_]+$/.test(this.newTemplateName) &&
          this.selectedLanguage &&
          this.bodyText &&
          this.bodyVariableErrors.length === 0
        );
      },
      bodyVariableErrors() {
        const errors = [];
        if (!this.bodyText) return errors;

        const trimmedText = this.bodyText.trim();

        if (/^\{\{\d+\}\}/.test(trimmedText)) {
          errors.push(this.$t('WHATSAPP_TEMPLATES.CREATOR.VALIDATION.BODY.STARTS_WITH_VAR'));
        }

        const lastVarMatch = trimmedText.match(/\{\{\d+\}\}(?![\s\S]*\{\{\d+\}\})/);
        if (lastVarMatch) {
          const posAfterLastVar = lastVarMatch.index + lastVarMatch[0].length;
          const textAfterLastVar = trimmedText.substring(posAfterLastVar);
          if (!/[a-zA-ZÀ-ÿ]+/.test(textAfterLastVar)) {
            errors.push(this.$t('WHATSAPP_TEMPLATES.CREATOR.VALIDATION.BODY.ENDS_WITH_VAR'));
          }
        }

        const numVars = this.variableNumbers.length;
        if (numVars > 0) {
          const withoutVars = trimmedText.replace(/\{\{\d+\}\}/g, ' ');
          const wordMatches = withoutVars.match(/[a-zA-Z0-9À-ÿ]+/g);
          const nonVarWordCount = wordMatches ? wordMatches.length : 0;
          const requiredNonVarWords = 3 + (numVars - 1) * 2;
          if (nonVarWordCount < requiredNonVarWords) {
            errors.push(this.$t('WHATSAPP_TEMPLATES.CREATOR.VALIDATION.BODY.TOO_MANY_VARS'));
          }
        }

        return errors;
      },
      activeAgentBot() {
        return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
      },
      availableButtonOptions() {
        if (this.buttons.length >= 10) return [];

        const exclusiveTypes = ['VOICE_CALL', 'PHONE_NUMBER'];
        const currentExclusiveType = this.buttons.find(b => exclusiveTypes.includes(b.type))?.type;

        return this.localizedButtonOptions.filter(opt => {
          if (opt.value === 'QUICK_REPLY' || opt.value === 'URL') return true;
          if (currentExclusiveType && exclusiveTypes.includes(opt.value) && opt.value !== currentExclusiveType) {
            return false;
          }

          return !this.buttons.some(b => b.type === opt.value);
        });
      },
      localizedButtonOptions() {
        return this.buttonOptionValues.map(val => ({
          value: val,
          label: this.$t(`WHATSAPP_TEMPLATES.CREATOR.BUTTONS.OPTIONS.${val}`),
        }));
      },
      nextVariableIndex() {
        let max = 0;
        const regex = /\{\{(\d+)\}\}/g;
        let match;
        while ((match = regex.exec(this.bodyText)) !== null) {
          const num = parseInt(match[1], 10);
          if (num > max) max = num;
        }
        return max + 1;
      },
      dropdownOptions() {
        const marketing = [
          { value: 'padrao', label: 'Padrão' },
          { value: 'catalogo', label: 'Catálogo' },
          { value: 'flows', label: 'Flows' },
          { value: 'detalhes_do_pedido', label: 'Detalhes do pedido' },
          {
            value: 'solicitacao_permissoes_ligacao',
            label: 'Solicitação para permissões para ligação',
          },
        ];
        const utilidades = [
          { value: 'padrao', label: 'Padrão' },
          { value: 'flows', label: 'Flows' },
          { value: 'order_status', label: 'Status do pedido' },
          { value: 'detalhes_do_pedido', label: 'Detalhes do pedido' },
          {
            value: 'solicitacao_permissoes_ligacao',
            label: 'Solicitação para permissões para ligação',
          },
        ];
        return this.selectedCategory === 'marketing' ? marketing : utilidades;
      },
      headerAccept() {
        switch (this.headerType) {
          case 'image':
            return 'image/png,image/gif,image/jpeg';
          case 'video':
            return 'video/mp4,video/avi,video/mpeg';
          case 'document':
            return 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          default:
            return '*/*';
        }
      },
      whatsAppTemplateMessages() {
        const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
          this.inbox.id
        );
        if (Array.isArray(templates)) {
          return templates
        }
        return [];
      },
      filteredTemplateMessages() {
        const lowerCaseQuery = this.query.toLowerCase();
        return this.whatsAppTemplateMessages.filter(template =>
          template.name.toLowerCase().includes(lowerCaseQuery)
        );
      },
      variableNumbers() {
        const found = new Set();
        const regex = /\{\{(\d+)\}\}/g;
        let match;
        while ((match = regex.exec(this.bodyText)) !== null) {
          found.add(parseInt(match[1], 10));
        }
        return Array.from(found).sort((a, b) => a - b);
      },
      headerMediaIsPdf() {
        if (!this.headerMediaFile) return false;
        return this.headerMediaFile.type === 'application/pdf';
      },
    },
    watch: {
      selectedCategory() {
        if (!this.dropdownOptions.find(o => o.value === this.selectedTemplateType)) {
          this.selectedTemplateType = this.dropdownOptions[0]?.value || '';
        }
      },
      bodyText() {
        this.syncVariableExamples();
      },
    },
    mounted() {
      this.$store.dispatch('agentBots/get');
      this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
      this.$store.dispatch('inboxes/revalidate', { newKey: null });
      this.syncVariableExamples();
    },

    methods: {
      redirectToMetaCategoryUpdates() {
        const businessId = this.inbox?.provider_config?.business_account_id;
        const appId = window.chatwootConfig.fbCompanyId;
        const base = `https://business.facebook.com/business-support-home/${appId}`;
        const url = `${base}/${encodeURIComponent(businessId)}/?templates_tab=category_updates`
        window.open(url, '_blank', 'noopener,noreferrer');
      },
      toggleEmojiPicker() {
        this.showEmojiPicker = !this.showEmojiPicker;
      },
      hideEmojiPicker() {
        this.showEmojiPicker = false;
      },
      emojiOnClick(emoji) {
        const textarea = this.$refs.bodyInput;
        if (!textarea) {
          this.bodyText += emoji;
          return;
        }
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const before = this.bodyText.slice(0, start);
        const after = this.bodyText.slice(end);
        this.bodyText = `${before}${emoji}${after}`;
        this.$nextTick(() => {
          const pos = before.length + emoji.length;
          textarea.setSelectionRange(pos, pos);
          textarea.focus();
        });
      },
      statusClass(template) {
        const s = (template?.status || '').toLowerCase();
        if (s === 'approved') return 'status-approved';
        if (s === 'pending') return 'status-pending';
        if (s === 'paused') return 'status-paused';
        return 'status-other';
      },
      ptStatus(template) {
        const s = (template?.status || '').toLowerCase();
        if (s === 'approved') return 'Aprovado';
        if (s === 'pending') return 'Pendente';
        if (s === 'rejected') return 'Rejeitado';
        if (s === 'paused') return 'Pausado';
        return 'Desconhecido';
      },
      onHeaderTypeChange() {
        this.headerMediaFile = null;
        this.headerMediaPreviewUrl = '';
        this.headerHandle = '';
        if (this.$refs.headerFileInput) {
          this.$refs.headerFileInput.value = '';
        }
      },
      onHeaderFileSelected(event) {
        const file = event?.target?.files?.[0];
        if (!file) return;
        this.headerMediaFile = file;
        const reader = new FileReader();
        reader.onload = e => {
          this.headerMediaPreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
        this.$nextTick(() => this.uploadHeaderToMeta());
      },
      async uploadHeaderToMeta() {
        if (!this.headerMediaFile) return;
        this.headerUploadState = 'uploading';
        const APP_ID = window.chatwootConfig.fbAppId;
        const USER_ACCESS_TOKEN = window.chatwootConfig.fbAppSecret;
        const fileName = encodeURIComponent(this.headerMediaFile.name);
        const fileType = encodeURIComponent(this.headerMediaFile.type || 'application/octet-stream');
        const fileLength = this.headerMediaFile.size;
        const url = `https://graph.facebook.com/v24.0/${APP_ID}/uploads?file_name=${fileName}&file_length=${fileLength}&file_type=${fileType}&access_token=${APP_ID}|${USER_ACCESS_TOKEN}`;
        try {
          const resp = await axios.post(url);
          this.headerHandle = resp?.data?.id;
          this.headerUploadState = this.headerHandle ? 'success' : 'error';
        } catch (e) {
          console.error('Erro no upload para Meta:', e);
          this.headerUploadState = 'error';
        }
      },
      onCreateTemplate() {
        this.isCreating = true;
      },
      onNameInput(event) {
        const original = event && event.target ? event.target.value : this.newTemplateName;
        if (typeof original !== 'string') {
          this.nameWarning = '';
          return;
        }
        const lower = original.toLowerCase();
        const noDiacritics = lower.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const spacesToUnderscore = noDiacritics.replace(/\s+/g, '_');
        const cleaned = spacesToUnderscore.replace(/[^a-z0-9_]/g, '');
        const changed = cleaned !== original;
        if (this.newTemplateName !== cleaned) {
          this.newTemplateName = cleaned;
        }
        this.nameWarning = changed
          ? 'O nome foi ajustado para o formato válido: minúsculas, números e _.'
          : '';
      },
      addButton() {
        if (!this.newButtonType) return;

        if (this.buttons.length >= 10) {
          this.$store.dispatch('alerts/show', {
            message: this.$t('WHATSAPP_TEMPLATES.CREATOR.BUTTONS.MAX_LIMIT'),
            type: 'error',
          });
          return;
        }

        const exclusiveTypes = ['VOICE_CALL', 'PHONE_NUMBER'];
        const currentExclusiveType = this.buttons.find(b => exclusiveTypes.includes(b.type))?.type;

        if (currentExclusiveType && exclusiveTypes.includes(this.newButtonType) && this.newButtonType !== currentExclusiveType) {
          this.$store.dispatch('alerts/show', {
            message: this.$t('WHATSAPP_TEMPLATES.CREATOR.BUTTONS.EXCLUSIVE_TYPE_ERROR'),
            type: 'error',
          });
          return;
        }

        if (this.newButtonType !== 'QUICK_REPLY' && this.newButtonType !== 'URL' && this.buttons.some(b => b.type === this.newButtonType)) return;

        this.buttons.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          type: this.newButtonType,
          inputValue: '',
          displayText: '',
          phoneNumber: '',
          dialCode: '+55',
        });
        this.newButtonType = '';
      },
      removeButton(id) {
        this.buttons = this.buttons.filter(b => b.id !== id);
      },
      onPhoneInputChange(btn, phoneNumber) {
        btn.phoneNumber = phoneNumber;
        const cleanPhone = phoneNumber.replace(/\s+/g, '');
        btn.inputValue = `${btn.dialCode}${cleanPhone}`;
      },
      onPhoneCodeChange(btn, dialCode) {
        btn.dialCode = dialCode;
        const cleanPhone = (btn.phoneNumber || '').replace(/\s+/g, '');
        btn.inputValue = `${dialCode}${cleanPhone}`;
      },
      dynamicLabelFor(type) {
        switch (type) {
          case 'URL':
            return 'Insira a URL do site';
          case 'VOICE_CALL':
            return 'Insira o número do WhatsApp com DDI e DDD';
          case 'PHONE_NUMBER':
            return 'Insira o número de telefone';
          case 'QUICK_REPLY':
          default:
            return 'Insira o texto do botão';
        }
      },
      dynamicPlaceholderFor(type) {
        switch (type) {
          case 'URL':
            return 'https://exemplo.com';
          case 'VOICE_CALL':
            return '+55 11 91234-5678';
          case 'PHONE_NUMBER':
            return '(11) 1234-5678';
          case 'QUICK_REPLY':
          default:
            return 'Texto do botão';
        }
      },
      labelForType(type) {
        const found = this.localizedButtonOptions.find(o => o.value === type);
        return found ? found.label : type;
      },
      showValueField(type) {

        return type === 'URL' || type === 'PHONE_NUMBER' ;
      },
      showTextField(type) {
        return type !== 'COPY_CODE';
      },
      applyFormat(type) {
        const textarea = this.$refs.bodyInput;
        if (!textarea) return;

        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const selectedText = this.bodyText.slice(start, end);

        if (!selectedText) return;

        let formattedText = '';
        let wrapperLength = 0;

        switch (type) {
          case 'bold':
            formattedText = `*${selectedText}*`;
            wrapperLength = 1;
            break;
          case 'italic':
            formattedText = `_${selectedText}_`;
            wrapperLength = 1;
            break;
          case 'strikethrough':
            formattedText = `~${selectedText}~`;
            wrapperLength = 1;
            break;
          case 'monospace':
            formattedText = `\`\`\`${selectedText}\`\`\``;
            wrapperLength = 3;
            break;
          default:
            return;
        }

        const before = this.bodyText.slice(0, start);
        const after = this.bodyText.slice(end);
        this.bodyText = `${before}${formattedText}${after}`;

        this.$nextTick(() => {
          const newStart = start + wrapperLength;
          const newEnd = newStart + selectedText.length;
          textarea.setSelectionRange(newStart, newEnd);
          textarea.focus();
        });
      },
      addVariable() {
        const textarea = this.$refs.bodyInput;
        const token = `{{${this.nextVariableIndex}}}`;
        if (!textarea) {
          this.bodyText += token;
          return;
        }
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const before = this.bodyText.slice(0, start);
        const after = this.bodyText.slice(end);
        this.bodyText = `${before}${token}${after}`;
        this.$nextTick(() => {
          const pos = before.length + token.length;
          textarea.setSelectionRange(pos, pos);
          textarea.focus();
        });
      },
      tokenFor(n) {
        return `{{${n}}}`;
      },
      syncVariableExamples() {
        const current = this.variableNumbers;
        const newExamples = current.map(num => {
          const prevIndex = this.prevVariableNumbers.indexOf(num);
          return prevIndex > -1 ? this.variableExamples[prevIndex] : '';
        });
        this.variableExamples = newExamples;
        this.prevVariableNumbers = current.slice();
      },
      getTemplateBody(template) {
        return template.components.find(component => component.type === 'BODY')
          .text;
      },
      resetFormFields() {
        this.newTemplateName = '';
        this.nameWarning = '';
        this.selectedLanguage = 'pt_BR';
        this.selectedCategory = 'marketing';
        this.selectedTemplateType = 'padrao';
        this.headerType = 'text';
        this.headerText = '';
        this.headerMediaFile = null;
        this.headerMediaPreviewUrl = '';
        this.headerHandle = '';
        this.headerUploadState = 'idle';
        this.bodyText = '';
        this.footerText = '';
        this.buttons = [];
        this.variableExamples = [];
        this.prevVariableNumbers = [];
        this.newButtonType = '';
        this.showEmojiPicker = false;
        if (this.$refs.headerFileInput) {
          this.$refs.headerFileInput.value = '';
        }
      },
      onSubmitTemplate() {
        if (!this.isFormValid) return;
        const form = new FormData();
        form.append('provider_config[business_account_id]', this.inbox?.provider_config?.business_account_id || '');
        form.append('provider_config[api_key]', this.inbox?.provider_config?.api_key || '');
        form.append('newTemplateName', this.newTemplateName);
        form.append('selectedLanguage', this.selectedLanguage);
        form.append('selectedCategory', this.selectedCategory);
        form.append('selectedTemplateType', this.selectedTemplateType);
        form.append('headerType', this.headerType);
        form.append('headerText', this.headerType === 'text' ? this.headerText : '');
        form.append('headerHandle', this.headerType !== 'text' ? (this.headerHandle || '') : '');
        form.append('bodyText', this.bodyText);
        form.append('footerText', this.footerText);
        (this.variableExamples || []).forEach(v => form.append('variableExamples[]', v));
        (this.buttons || []).forEach((b, idx) => {
          form.append(`buttons[${idx}][type]`, b.type || '');
          form.append(`buttons[${idx}][displayText]`, b.displayText || '');
          form.append(`buttons[${idx}][inputValue]`, b.inputValue || '');
        });
        if (this.headerType !== 'text' && this.headerMediaFile) {
          form.append('headerMedia[file]', this.headerMediaFile, this.headerMediaFile.name);
          form.append('headerMedia[fileName]', this.headerMediaFile.name);
          form.append('headerMedia[fileType]', this.headerMediaFile.type);
          form.append('headerMedia[fileLength]', this.headerMediaFile.size);
        }

        this.isSubmitting = true;
        this.$store.dispatch('templates/create', form)
          .then(() => {
            const syncPayload = {
              session_id: this.inbox.session_id,
            }
            this.$store.dispatch('inboxes/syncTemplateInbox', syncPayload)
            this.showAlert(this.$t('WHATSAPP_TEMPLATES.CREATOR.ALERTS.SAVE_SUCCESS'));
            this.resetFormFields();
            this.isCreating = false;
          })
          .catch((error) => {
            console.error('Erro no submit do template:', error);
            this.showAlert(this.$t('WHATSAPP_TEMPLATES.CREATOR.ALERTS.SAVE_ERROR'));
          })
          .finally(() => {
            this.isSubmitting = false;
          });
      },
      openDeleteModal(template) {
        this.templateToDelete = template;
        this.showDeleteModal = true;
      },
      closeDeleteModal() {
        this.showDeleteModal = false;
        this.templateToDelete = null;
      },
      async confirmDeleteTemplate() {
        if (!this.templateToDelete) return;

        const templateName = this.templateToDelete.name;
        const providerConfig = {
          business_account_id: this.inbox?.provider_config?.business_account_id,
          api_key: this.inbox?.provider_config?.api_key,
        };

        this.closeDeleteModal();

        this.isSyncingAfterDelete = true;

        try {
          await this.$store.dispatch('templates/deleteTemplate', {
            name: templateName,
            providerConfig: providerConfig,
          });

          const syncPayload = {
            session_id: this.inbox.session_id,
          };
          await this.$store.dispatch('inboxes/syncTemplateInbox', syncPayload);

          this.showAlert(this.$t('WHATSAPP_TEMPLATES.DELETE.SUCCESS'));
        } catch (error) {
          console.error('Erro ao deletar template:', error);
          this.showAlert(this.$t('WHATSAPP_TEMPLATES.DELETE.ERROR'));
        } finally {
          this.isSyncingAfterDelete = false;
        }
      },
      openPdfInNewTab() {
        if (!this.headerMediaPreviewUrl) return;

        if (this.headerMediaPreviewUrl.startsWith('data:')) {
          const base64Data = this.headerMediaPreviewUrl.split(',')[1];
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }

          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);

          window.open(blobUrl, '_blank', 'noopener,noreferrer');

          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 100);
        } else {
          window.open(this.headerMediaPreviewUrl, '_blank', 'noopener,noreferrer');
        }
      },
    },
  };
  </script>

  <style scoped lang="scss">
  .button--disconnect {
    margin-left: var(--space-small);
  }
  .actions-row {
    min-width: 83vw;
    display: flex;
    justify-content: flex-end;
    margin-bottom: var(--space-one);
  }
  .field-group {
    margin-top: var(--space-two);
  }
  .field-label {
    display: block;
    font-weight: var(--font-weight-medium);
    margin-bottom: var(--space-micro);
  }
  .text-input {
    width: 100%;
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-medium);
    padding: var(--space-small);
    background: var(--white);
  }
  .textarea {
    width: 100%;
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-medium);
    padding: var(--space-small);
    background: var(--white);
  }
  .field-meta {
    display: flex;
    justify-content: flex-end;
    margin-top: var(--space-micro);
  }
  .formatting-buttons {
    display: flex;
    gap: var(--space-micro);
    margin-top: var(--space-micro);

    .format-btn {
      padding: var(--space-micro) var(--space-small);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-small);
      background: var(--white);
      cursor: pointer;
      font-size: var(--font-size-mini);
      transition: all 0.2s ease;

      &:hover {
        background: var(--s-50);
        border-color: var(--s-300);
      }

      &:active {
        background: var(--s-100);
      }
    }

    .add-var-link {
      align-self: center;
      margin-left: var(--space-one);
      color: var(--b-600);
      cursor: pointer;
      font-size: var(--font-size-mini);
      text-decoration: underline;
    }
  }
  .formatting-wrap {
    position: relative;
    display: inline-block;
  }
  .emoji-dialog {
    position: absolute;
    z-index: var(--z-index-normal);
    top: calc(100% + 4px);
    left: 0;
  }

  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--space-micro);
  }
  .char-counter {
    color: var(--s-400);
    font-size: var(--font-size-mini);
  }
  .button-input-group {
    margin-top: var(--space-one);
  }
  .variables-examples {
    margin-top: var(--space-one);
  }
  .variable-example-row {
    margin-top: var(--space-micro);
  }
  .flex-between {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--space-one);
  }
  .templates__list-search {
    align-items: center;
    background-color: var(--s-25);
    border-radius: var(--border-radius-medium);
    border: 1px solid var(--s-100);
    display: flex;
    margin-bottom: var(--space-one);
    padding: 0 var(--space-one);

    .search-icon {
      color: var(--s-400);
    }

    .templates__search-input {
      background-color: transparent;
      border: none;
      font-size: var(--font-size-mini);
      height: unset;
      margin: 0;
      width: 100%;
    }
  }
  .template__list-container {
    border-radius: var(--border-radius-medium);
    width: 100%;
    min-height: 100px;
    height: auto;
    max-height: none;
    overflow: visible;
    padding: var(--space-one);

    .template__list-item {
      border-radius: var(--border-radius-medium);
      cursor: pointer;
      display: block;
      padding: var(--space-one);
      text-align: left;
      width: 100%;

      &.status-pending {
        background-color: #fff8e6; /* leve amarelo */
      }
      &.status-approved {
        background-color: #e9f9ee; /* leve verde */
      }
      &.status-paused {
        background-color: #f5f5f5; /* cinza claro */
      }
      &.status-other {
        background-color: #ffecee; /* leve vermelho */
      }

      &:hover {
        background-color: var(--w-50);
      }

      .label-title {
        font-size: var(--font-size-small);
      }

      .label-category {
        margin-top: var(--space-two);

        span {
          font-size: var(--font-size-small);
          font-weight: var(--font-weight-bold);
        }
      }

      .label-body {
        font-family: monospace;
      }
    }
  }
  .strong {
    font-size: var(--font-size-mini);
    font-weight: var(--font-weight-bold);
  }
  hr {
    border-bottom: 1px solid var(--s-100);
    margin: var(--space-one) auto;
    max-width: 95%;
  }

  .create-template-container {
    padding: var(--space-one);
  }

  .create-selections {
    margin-bottom: var(--space-one);
  }

  .radio-group {
    display: flex;
    gap: var(--space-two);
    margin-bottom: var(--space-one);

    .radio-option {
      display: flex;
      align-items: center;
      gap: var(--space-micro);
      cursor: pointer;

      input[type="radio"] {
        cursor: pointer;
      }
    }
  }

  .dropdown-group {
    margin-bottom: var(--space-one);
  }

  .select-input {
    width: 100%;
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-medium);
    padding: var(--space-small);
    background: var(--white);
  }

  .helper-text {
    font-size: var(--font-size-mini);
    color: var(--s-500);
    margin-top: var(--space-micro);
  }

  .warning-text {
    font-size: var(--font-size-mini);
    color: var(--y-600);
    margin-top: var(--space-micro);
  }

  .error-text {
    font-size: var(--font-size-mini);
    color: var(--r-600);
    margin-top: var(--space-micro);
    font-weight: var(--font-weight-medium);
  }

  .create-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: var(--space-two);
    gap: var(--space-small);
  }
  .emoji-dialog {
    position: absolute;
    z-index: var(--z-index-normal);
    margin-top: var(--space-micro);
  }

  .template-actions {
    display: flex;
    align-items: center;
    gap: var(--space-small);
  }

  .delete-button {
    display: flex;
    align-items: center;
    gap: var(--space-micro);
    padding: var(--space-micro) var(--space-small);
    font-size: var(--font-size-mini);

    &:hover:not(:disabled) {
      background-color: var(--r-600);
    }
  }

  .syncing-container {
    width: 100%;
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .syncing-overlay {
    background: var(--s-50);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border-radius: var(--border-radius-medium);
    gap: var(--space-small);
    padding: var(--space-large);
    border: 1px solid var(--s-100);
    min-height: 100px;
    min-width: 80vw;

    .syncing-text {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-medium);
      color: var(--s-700);
      margin: 0;
    }
  }

  .pdf-preview-button {
    background: none;
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-small);
    padding: 0;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-block;

    &:hover {
      border-color: var(--s-300);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }
  </style>
